import { useEffect, useState, useRef, useCallback } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, PlayCircle, Search, MessageSquare, Lock, Paperclip, Loader2 } from "lucide-react";
import { openWS } from "@/lib/ws";

const resolve = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export default function Messages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // {id, name}
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [wsLive, setWsLive] = useState(false);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/conversations");
      setConvs(data);
    } catch (err) { console.error("Failed to load conversations:", err); }
  }, []);

  const loadMsgs = useCallback(async (id, opts = {}) => {
    try {
      const { data } = await api.get(`/messages/${id}`);
      setMsgs((prev) => {
        const shouldScroll = opts.forceScroll || data.length > prev.length;
        if (shouldScroll) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
        return data;
      });
    } catch (err) { console.error("Failed to load messages:", err); }
  }, []);

  // Initial load + polling fallback for the conversation list.
  useEffect(() => { if (!user) return; loadConvs(); }, [user, loadConvs]);
  useEffect(() => {
    if (!user || wsLive) return;
    const t = setInterval(loadConvs, 3000);
    return () => clearInterval(t);
  }, [user, wsLive, loadConvs]);

  // Initial load for the open conversation + polling fallback when WS is down.
  useEffect(() => { if (!active) return; loadMsgs(active.id, { forceScroll: true }); }, [active, loadMsgs]);
  useEffect(() => {
    if (!active || wsLive) return;
    const t = setInterval(() => loadMsgs(active.id), 3000);
    return () => clearInterval(t);
  }, [active, wsLive, loadMsgs]);

  // Realtime: subscribe to DM WS for the whole logged-in session.
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("eco_token");
    const conn = openWS("/messages", (data) => {
      if (data?.type !== "dm" || !data.message) return;
      const msg = data.message;
      // If it's for the currently-open thread, append to msgs
      if (active && (msg.from_id === active.id || msg.to_id === active.id)) {
        setMsgs((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
      }
      // Always refresh conv list (unread + last message)
      loadConvs();
    }, { token, onOpen: () => setWsLive(true), onClose: () => setWsLive(false) });
    return () => conn.close();
  }, [user, active, loadConvs]);

  useEffect(() => {
    const to = params.get("to");
    const name = params.get("name");
    if (to && name) { setActive({ id: to, name }); setParams({}); }
  }, [params, setParams]);

  useEffect(() => {
    if (!searchQ) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/users/search", { params: { q: searchQ } }); setSearchResults(data); } catch (err) { console.error("Message user search failed:", err); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  const send = async () => {
    if (!active) return;
    if (!text.trim()) return;
    try {
      await api.post("/messages", { to_id: active.id, text });
      setText("");
      loadMsgs(active.id);
      loadConvs();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const attachVideo = async (file) => {
    if (!file || !active) return;
    if (!file.type.startsWith("video/")) return toast.error("Video files only");
    if (file.size > 25 * 1024 * 1024) return toast.error("Max 25 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/messages/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/messages", { to_id: active.id, text, chat_video_url: data.video_url });
      setText("");
      loadMsgs(active.id);
      loadConvs();
      toast.success("Video sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!user) return <Navigate to="/login" />;

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <div className="mono text-xs text-primary flex items-center gap-2">// messages <Lock className="h-3 w-3" data-testid="messages-encrypted-badge" /> end-to-end encrypted at rest</div>
      <h1 className="mt-2 text-4xl font-black tracking-tighter">Direct messages.</h1>

      <div className="mt-6 grid gap-px bg-border md:grid-cols-[300px_1fr] border border-border" style={{ minHeight: "70vh" }}>
        {/* left: conversations + search */}
        <aside className="bg-background flex flex-col">
          <div className="p-3 border-b border-border">
            <div className="flex items-center gap-2 border border-border px-2 py-1">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input data-testid="msg-user-search" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Find people…" className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 border border-border">
                {searchResults.map((u) => (
                  <button key={u.id} data-testid={`msg-start-${u.id}`} onClick={() => { setActive({ id: u.id, name: u.display_name }); setSearchQ(""); setSearchResults([]); }} className="w-full flex items-center gap-2 p-2 text-left hover:bg-primary/5">
                    <div className="flex h-7 w-7 items-center justify-center bg-primary/20 text-xs font-bold text-primary">{u.display_name?.[0]?.toUpperCase()}</div>
                    <div className="text-sm">{u.display_name}</div>
                    <div className="ml-auto text-[10px] uppercase tracking-widest text-muted-foreground">{u.role}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {convs.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">No conversations yet. Search someone above to start.</div>
            ) : convs.map((c) => (
              <button key={c.other_id} data-testid={`conv-${c.other_id}`} onClick={() => setActive({ id: c.other_id, name: c.other_name })} className={`w-full text-left border-b border-border p-3 hover:bg-primary/5 ${active?.id === c.other_id ? "bg-primary/5" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center bg-primary/20 text-xs font-bold text-primary">{c.other_name?.[0]?.toUpperCase()}</div>
                    <div className="font-bold text-sm">{c.other_name}</div>
                  </div>
                  {c.unread > 0 && <span className="mono text-[10px] bg-primary text-primary-foreground px-1.5">{c.unread}</span>}
                </div>
                <div className="mt-1 text-xs text-muted-foreground line-clamp-1">
                  {c.last_from === user.id ? "You: " : ""}{c.last_video ? "📹 " : ""}{c.last_text || (c.last_video ? c.last_video.title : "")}
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* right: conversation */}
        <section className="bg-background flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="mx-auto h-10 w-10 text-primary/60" />
                <div className="mt-4 text-sm">Pick a conversation or search for someone to message.</div>
              </div>
            </div>
          ) : (
            <>
              <div className="border-b border-border p-4 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center bg-primary/20 text-sm font-bold text-primary">{active.name?.[0]?.toUpperCase()}</div>
                <div className="font-bold tracking-tight flex-1">{active.name}</div>
                {wsLive && <span data-testid="dm-live-badge" className="mono text-[9px] uppercase tracking-widest text-primary">● live</span>}
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-primary"><Lock className="h-3 w-3" />encrypted</div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {msgs.map((m) => (
                  <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${m.from_id === user.id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] border p-3 ${m.from_id === user.id ? "border-primary bg-primary/10" : "border-border"}`}>
                      {m.video && (
                        <a href={`/learn`} className="mb-2 flex items-center gap-2 border border-border bg-background p-2 hover:border-primary/50">
                          <div className="relative h-14 w-24 shrink-0 bg-muted overflow-hidden">
                            {m.video.thumbnail_url ? <img src={m.video.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-secondary to-muted" />}
                            <div className="absolute inset-0 flex items-center justify-center"><PlayCircle className="h-5 w-5 text-primary" /></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-bold line-clamp-2">{m.video.title}</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.video.kind} · {m.video.author_name}</div>
                          </div>
                        </a>
                      )}
                      {m.chat_video && (
                        <video data-testid={`chat-video-${m.id}`} src={resolve(m.chat_video.video_url)} controls className="mb-2 w-full max-w-[280px] aspect-video object-cover border border-border" />
                      )}
                      {m.text && <div className="text-sm whitespace-pre-wrap">{m.text}</div>}
                      <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="border-t border-border p-3 flex items-center gap-2">
                <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={(e) => attachVideo(e.target.files?.[0])} />
                <Button data-testid="msg-attach" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-none">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                </Button>
                <Input data-testid="msg-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={`Message ${active.name}…`} className="rounded-none bg-input" />
                <Button data-testid="msg-send" onClick={send} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Send className="h-4 w-4" /></Button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
